import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { FeatureLabel3D } from 'features/assetViz/objects/features/procedural/FeatureLabel3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from 'features/assetViz/objects/features/procedural/SurfaceLineFeature3D'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { HtmlOverlayAnchor } from 'features/assetViz/utils/helpers'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentPath,
  GeckoPipeSeamWeld,
} from 'types'

/**
 * A component used to render a single pipe seam weld on a component's surface
 * @param model             The asset model for this pipe seam weld
 * @constructor
 */
export const PipeSeamWeldFeature3D: React.FC<{
  model: GeckoPipeSeamWeld
}> = ({ model }) => {
  const { show3D } = useAssetVizInternal()
  const { bounds } = useSurfaceProps()
  const { path, weldNamePlateCoord, originalAnchor } = React.useMemo(() => {
    const path = GeckoComponentPath.CreateFromVerticalLine(
      model.feature_geometry
    )

    const y_length = model.feature_geometry.end.y.minus(
      model.feature_geometry.start.y
    )
    const midPoint = model.feature_geometry.start.y.plus(y_length.scale(0.5))
    const weldNamePlateCoord = GeckoComponentCoords.CreateFromMeasurements(
      model.feature_geometry.componentID,
      model.feature_geometry.start.x,
      midPoint
    )
    let originalAnchor = 'bottom'

    // Name plate is too close to the top, so flip it
    if (
      Math.abs(model.feature_geometry.end.x.minus(bounds.start.x).num) <=
        0.28 &&
      !show3D
    ) {
      originalAnchor = 'top'
    }

    return {
      path: path,
      weldNamePlateCoord: weldNamePlateCoord,
      originalAnchor: originalAnchor as HtmlOverlayAnchor,
    }
  }, [model.feature_geometry, show3D, bounds.start.x])

  return (
    <group name={model.id}>
      <SurfaceLineFeature3D
        path={path}
        featureModel={model}
        lineSelectionType={SurfaceLineSelectionType.SelectableFeatureLine}
      />
      <FeatureLabel3D
        feature={model}
        coords={weldNamePlateCoord}
        anchor={originalAnchor}
        showBoundaryLabelIn2D
        transpose
      />
    </group>
  )
}
